/**
 * Google Tag Manager dataLayer utilities for analytics tracking.
 * Provides type-safe methods for pushing events to GTM.
 */

declare global {
	interface Window {
		dataLayer: Record<string, unknown>[];
	}
}

export type GA4Item = {
	item_id: string;
	item_name: string;
	item_category?: string;
	item_variant?: string;
	index?: number;
	price: number;
	discount?: number;
	quantity: number;
};

export type GA4SelectItemData = {
	ecommerce: {
		currency: string;
		item_list_id: string;
		item_list_name: string;
		items: GA4Item[];
	};
};

export type GA4AddPaymentInfoData = {
	ecommerce: {
		currency: string;
		value: number;
		items: GA4Item[];
	};
};

export type GA4BeginCheckoutData = {
	ecommerce: {
		currency: string;
		value: number;
		items: GA4Item[];
	};
};

export type GA4PurchaseData = {
	ecommerce: {
		transaction_id: string;
		currency: string;
		value: number;
		tax: number;
		shipping: number;
		// Optional: an empty string is collected and reports as (not set), which reads
		// identically to a broken tag.
		coupon?: string;
		items: GA4Item[];
	};
};

/**
 * Safely pushes an event to the GTM dataLayer.
 * Handles SSR by checking for window availability.
 * Ensures dataLayer array exists before pushing.
 *
 * @param event - The event name (e.g., 'begin_checkout', 'purchase')
 * @param data - Optional event data/parameters
 * @returns boolean - true if the push succeeded, so callers only mark a one-shot flag then
 */
export function pushToDataLayer(event: string, data?: Record<string, unknown>): boolean {
	if (typeof window === 'undefined') {
		return false;
	}

	// GTM replaces dataLayer.push, so a broken tag throws back into the caller. These fire
	// from inside checkout, where the catch reports "Payment failed" — never cost a sale.
	try {
		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push({ event, ...data });
		return true;
	} catch {
		return false;
	}
}

/**
 * Pushes an ecommerce event, preceded by the `ecommerce: null` clear Google requires.
 *
 * One try covers both: the dataLayer is a merge, so a failed clear followed by a successful
 * push would carry stale items into the new event. Losing the event beats sending it wrong.
 */
function pushEcommerceEvent(event: string, data: Record<string, unknown>): boolean {
	if (typeof window === 'undefined') {
		return false;
	}

	try {
		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push({ ecommerce: null });
		window.dataLayer.push({ event, ...data });
		return true;
	} catch {
		// The event is lost rather than sent with stale data; false leaves it eligible to retry.
		return false;
	}
}

/**
 * One-shot flags that stop an event being sent twice.
 *
 * The getter keeps the bare `localStorage`/`sessionStorage` access inside the try, which
 * throws SecurityError when site data is blocked. The memory Set covers the page when
 * storage is unavailable or full, since these run from effects that re-run on dependency
 * changes.
 */
const sentInMemory = new Set<string>();

function hasSentFlag(getStorage: () => Storage, key: string): boolean {
	if (sentInMemory.has(key)) {
		return true;
	}

	try {
		return !!getStorage().getItem(key);
	} catch {
		return false;
	}
}

function markSentFlag(getStorage: () => Storage, key: string): void {
	sentInMemory.add(key);

	try {
		getStorage().setItem(key, 'true');
	} catch {
		// Storage unavailable or full — the memory flag still covers this page session.
	}
}

function clearSentFlag(getStorage: () => Storage, key: string): void {
	sentInMemory.delete(key);

	try {
		getStorage().removeItem(key);
	} catch {}
}

/**
 * Fires the metered_paywall_email_capture event when a user
 * submits their email on the free article limit modal.
 */
export function trackMeteredPaywallEmailCapture(): void {
	pushToDataLayer('metered_paywall_email_capture');
}

/**
 * The paywalls are modals with no URL of their own, so a page view cannot stand in for
 * these — they fire when the paywall is actually on screen.
 */
export function trackMeteredPaywallReached(): void {
	pushToDataLayer('metered_paywall_reached');
}

/**
 * The article-limit wall only: the funnel step for a new visitor out of free articles.
 * Not deduplicated: event count means appearances, and GA4's totalUsers gives the
 * per-person rate without discarding the repeats.
 */
export function trackHardPaywallReached(): void {
	pushToDataLayer('hard_paywall_reached');
}

export function trackHardPaywallCtaClick(): void {
	pushToDataLayer('hard_paywall_cta_click');
}

/**
 * The plan-required modal (expired plan, Basic on Premium content, no plan). Kept apart
 * from hard_paywall_reached so paying and lapsed members don't inflate the funnel.
 */
export function trackSubscriptionWallReached(): void {
	pushToDataLayer('subscription_wall_reached');
}

export function trackSubscriptionWallCtaClick(): void {
	pushToDataLayer('subscription_wall_cta_click');
}

/**
 * Deliberately not deduplicated: people retake the quiz, and suppressing repeat starts
 * would misstate the rate against quiz_complete.
 */
export function trackQuizStart(): void {
	pushToDataLayer('quiz_start');
}

export function trackSelectItem(data: GA4SelectItemData): void {
	pushEcommerceEvent('select_item', data);
}

/**
 * Not deduplicated: a retry after a declined card is a genuine second attempt.
 */
export function trackAddPaymentInfo(data: GA4AddPaymentInfoData): void {
	pushEcommerceEvent('add_payment_info', data);
}

/**
 * Fires the create_account event when a new user account is created.
 *
 * Note: Unlike other tracking functions, this does not use sessionStorage deduplication.
 * This is intentional because:
 * 1. The event fires inside form submit callbacks, not useEffects - page refresh won't re-trigger it.
 * 2. Callers guard the call with `if (isNewUser)` or `if (!user)` checks that prevent re-firing
 *    after the user is authenticated.
 */
export function trackCreateAccount(): void {
	pushToDataLayer('create_account');
}

/**
 * Fires the quiz_complete event when a user completes the Find Essentials quiz.
 * Uses sessionStorage for deduplication to prevent re-firing on page refresh.
 *
 * @param ageId - The age category ID from the quiz
 * @param challengeId - Optional challenge category ID from the quiz
 * @returns boolean - true if event was fired, false if deduplicated
 */
export function trackQuizComplete(ageId: string, challengeId?: string): boolean {
	if (typeof window === 'undefined') {
		return false;
	}

	const key = `quiz_complete_${ageId}_${challengeId ?? 'none'}`;
	if (hasSentFlag(() => sessionStorage, key)) {
		return false;
	}

	if (!pushToDataLayer('quiz_complete')) {
		return false;
	}
	markSentFlag(() => sessionStorage, key);
	return true;
}

/**
 * Fires the begin_checkout GA4 event when a user starts the checkout process.
 *
 * Deduplicated on the plan, not the payment id: applying a coupon mints a new payment row,
 * and that is not a second checkout. Picking a different plan still fires.
 *
 * @param checkoutKey - Stable identifier for this checkout, i.e. the subscription type id
 * @param data - The checkout data including currency, value, and items
 * @returns boolean - true if event was fired, false if deduplicated
 */
export function trackBeginCheckout(checkoutKey: string, data: GA4BeginCheckoutData): boolean {
	if (typeof window === 'undefined') {
		return false;
	}

	const key = `begin_checkout_${checkoutKey}`;
	if (hasSentFlag(() => sessionStorage, key)) {
		return false;
	}

	if (!pushEcommerceEvent('begin_checkout', data)) {
		return false;
	}
	markSentFlag(() => sessionStorage, key);
	return true;
}

/**
 * Starts a new checkout attempt: called when a plan is picked, so an abandoned checkout that
 * is begun again in the same tab counts again. Coupons and payment retries don't pass through
 * a plan pick, so they stay deduplicated.
 */
export function resetBeginCheckout(checkoutKey: string): void {
	if (typeof window === 'undefined') {
		return;
	}

	clearSentFlag(() => sessionStorage, `begin_checkout_${checkoutKey}`);
}

/**
 * Fires the purchase GA4 event when a purchase is confirmed.
 * Uses localStorage for deduplication keyed by payment ID.
 * Critical to prevent duplicate conversion tracking.
 *
 * localStorage, not sessionStorage: sessionStorage is per-tab, so reopening the completion
 * URL in a second tab re-sent the sale.
 *
 * @param paymentId - The payment ID for deduplication
 * @param data - The purchase data including transaction_id, value, items, etc.
 * @returns boolean - true if event was fired, false if deduplicated
 */
export function trackPurchase(paymentId: string, data: GA4PurchaseData): boolean {
	if (typeof window === 'undefined') {
		return false;
	}

	// Google deduplicates every purchase with transaction_id="" into a single transaction, so
	// an empty id silently collapses all revenue rather than mislabelling one sale.
	if (!data.ecommerce.transaction_id) {
		console.error('purchase not sent: empty transaction_id');
		return false;
	}

	const key = `purchase_${paymentId}`;
	if (hasSentFlag(() => localStorage, key)) {
		return false;
	}

	if (!pushEcommerceEvent('purchase', data)) {
		return false;
	}
	markSentFlag(() => localStorage, key);
	return true;
}
